import React, { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Button,
  Tooltip,
} from '@mui/material';
import { Close, RestartAlt, Save } from '@mui/icons-material';
import { getAiMemory, setAiMemory, resetAiMemory, DEFAULT_AI_MEMORY } from '../../services/aiMemoryService';

interface Props {
  open: boolean;
  onClose: () => void;
}

const AiMemoryModal = ({ open, onClose }: Props) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [content, setContent] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (open) {
      setContent(getAiMemory());
      setSaved(false);
    }
  }, [open]);

  const handleSave = () => {
    setAiMemory(content);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleReset = () => {
    setContent(DEFAULT_AI_MEMORY);
    resetAiMemory();
  };

  const charCount = content.length;
  const tokenEstimate = Math.round(charCount / 4);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { height: '85vh' } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: `2px solid #4ECDC4`,
          pb: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1,
              backgroundColor: 'rgba(78,205,196,0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 18,
            }}
          >
            🧠
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              Пам'ять AI Асистента
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Правила та контекст, які AI читає перед кожною відповіддю
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box
          sx={{
            px: 2,
            py: 1,
            backgroundColor: isDark ? '#1A2A2A' : '#F0FAFA',
            borderBottom: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            gap: 2,
            alignItems: 'center',
          }}
        >
          <Typography variant="caption" color="textSecondary">
            📝 Markdown-форматування підтримується
          </Typography>
          <Typography variant="caption" color="textSecondary">
            ·
          </Typography>
          <Typography variant="caption" color="textSecondary">
            ~{tokenEstimate.toLocaleString()} токенів (~${((tokenEstimate / 1_000_000) * 0.15).toFixed(4)} за запит)
          </Typography>
          <Typography variant="caption" color="textSecondary">
            ·
          </Typography>
          <Typography variant="caption" sx={{ color: '#4ECDC4', fontWeight: 600 }}>
            GPT-4o mini
          </Typography>
        </Box>

        <Box
          component="textarea"
          value={content}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setContent(e.target.value)}
          spellCheck={false}
          sx={{
            flex: 1,
            width: '100%',
            border: 'none',
            outline: 'none',
            resize: 'none',
            p: 2.5,
            fontFamily: '"JetBrains Mono", "Fira Code", "Consolas", monospace',
            fontSize: 13,
            lineHeight: 1.7,
            backgroundColor: isDark ? '#111' : '#FAFAFA',
            color: theme.palette.text.primary,
            boxSizing: 'border-box',
          }}
        />
      </DialogContent>

      <DialogActions
        sx={{
          px: 2.5,
          py: 1.5,
          borderTop: `1px solid ${theme.palette.divider}`,
          justifyContent: 'space-between',
        }}
      >
        <Tooltip title="Відновити приклад за замовчуванням">
          <Button
            size="small"
            startIcon={<RestartAlt />}
            onClick={handleReset}
            color="inherit"
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            Скинути до прикладу
          </Button>
        </Tooltip>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {saved && (
            <Typography variant="caption" sx={{ color: '#4CAF50', fontWeight: 600 }}>
              ✓ Збережено
            </Typography>
          )}
          <Button
            onClick={onClose}
            variant="outlined"
            size="small"
            sx={{ textTransform: 'none' }}
          >
            Скасувати
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            size="small"
            startIcon={<Save />}
            sx={{
              backgroundColor: '#4ECDC4',
              '&:hover': { backgroundColor: '#3BA99F' },
              textTransform: 'none',
            }}
          >
            Зберегти
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default AiMemoryModal;
